import { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import WorkerAvatar from "@/components/WorkerAvatar";
import { LOKASI_OPTIONS, PENDIDIKAN_OPTIONS, GENDER_OPTIONS, STATUS_OPTIONS, OPSI_OPTIONS, LEVEL_OPTIONS } from "@/lib/constants";
import { api } from "@/context/AuthContext";
import { Loader2, Camera, Trash2, Upload } from "lucide-react";
import SertifikasiSection, { isSertifikat } from "@/components/workers/SertifikasiSection";

const EMPTY = {
  nomor_pegawai: "", nama: "", jabatan: "", tanggal_masuk: "", tanggal_akhir_kontrak: "", tanggal_lahir: "",
  usia: "", alamat: "", nomor_ktp: "", bpjs_kesehatan: "", bpjs_ketenagakerjaan: "",
  link_maps: "", lokasi_kerja: "", pendidikan: "", gender: "", status: "AKTIF",
  level_pekerja: "", ukuran_baju: "", ukuran_celana: "", ukuran_sepatu: "",
  nomor_pertalife: "", pilihan_opsi: "", keterangan: "", keterangan_status: "", punya_sertifikasi: false,
};

const STATUS_TRIGGER_TONE = {
  AKTIF: "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 [&_svg]:text-white",
  PENSIUN: "bg-amber-500 border-amber-500 text-white hover:bg-amber-600 [&_svg]:text-white",
  RESIGN: "bg-slate-600 border-slate-600 text-white hover:bg-slate-700 [&_svg]:text-white",
  PHK: "bg-rose-600 border-rose-600 text-white hover:bg-rose-700 [&_svg]:text-white",
};

function calcAge(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d)) return "";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 ? age : "";
}

// Tanggal akhir kontrak otomatis = tanggal lahir + usia pensiun (default 55 tahun)
function calcAkhirKontrak(dob, usiaPensiun) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d)) return "";
  d.setFullYear(d.getFullYear() + (parseInt(usiaPensiun, 10) || 55));
  return d.toISOString().slice(0, 10);
}

// Didefinisikan DI LUAR komponen agar tidak dibuat ulang tiap render
// (definisi di dalam menyebabkan input remount & kehilangan fokus setiap ketikan)
const Field = ({ form, set, label, k, type = "text", placeholder, required, disabled }) => (
  <div className="space-y-1.5">
    <Label htmlFor={k} className="text-sm">{label}{required && <span className="text-rose-500"> *</span>}</Label>
    <Input id={k} type={type} value={form[k] ?? ""} placeholder={placeholder} disabled={disabled}
      onChange={(e) => set(k, e.target.value)} data-testid={`field-${k}`}
      className={`h-10 ${disabled ? "bg-slate-50 text-slate-500" : ""}`} />
  </div>
);

const SelectField = ({ form, set, label, k, options, required }) => (
  <div className="space-y-1.5">
    <Label className="text-sm">{label}{required && <span className="text-rose-500"> *</span>}</Label>
    <Select value={form[k] || ""} onValueChange={(v) => set(k, v)}>
      <SelectTrigger data-testid={`field-${k}`} className="h-10"><SelectValue placeholder="Pilih..." /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o} data-testid={`option-${k}-${o}`}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export default function WorkerFormDialog({ open, onOpenChange, worker, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [removePhoto, setRemovePhoto] = useState(false);
  const [lokasiList, setLokasiList] = useState(LOKASI_OPTIONS);
  const [usiaPensiun, setUsiaPensiun] = useState(55);
  const [statusDocFile, setStatusDocFile] = useState(null);
  const [sertifikatBaru, setSertifikatBaru] = useState([]);
  const [hapusSertifikat, setHapusSertifikat] = useState([]);
  const [berlakuEdit, setBerlakuEdit] = useState({});
  const photoInputRef = useRef(null);
  const statusDocRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(worker ? { ...EMPTY, ...worker, usia: worker.usia ?? "" } : EMPTY);
      setError("");
      setPhotoFile(null);
      setPhotoPreview("");
      setRemovePhoto(false);
      setStatusDocFile(null);
      setSertifikatBaru([]);
      setHapusSertifikat([]);
      setBerlakuEdit({});
      api.get("/lokasi").then((res) => setLokasiList(res.data)).catch(() => {});
      api.get("/settings").then((res) => setUsiaPensiun(res.data.usia_pensiun || 55)).catch(() => {});
    }
  }, [open, worker]);

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("File harus berupa gambar."); return; }
    if (file.size > 5 * 1024 * 1024) { setError("Ukuran foto maksimal 5 MB."); return; }
    setError("");
    setPhotoFile(file);
    setRemovePhoto(false);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview("");
    if (worker?.photo_path) setRemovePhoto(true);
  };

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "tanggal_lahir") {
      next.usia = calcAge(v);
      next.tanggal_akhir_kontrak = calcAkhirKontrak(v, usiaPensiun);
    }
    return next;
  });

  const pickStatusDoc = (e) => {
    const file = e.target.files?.[0];
    if (statusDocRef.current) statusDocRef.current.value = "";
    if (!file) return;
    const okType = file.type === "application/pdf" || ["image/jpeg", "image/png", "image/webp"].includes(file.type);
    if (!okType) { setError("Dokumen bukti harus berupa PDF, JPG, PNG, atau WEBP."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("Ukuran dokumen bukti maksimal 10 MB."); return; }
    setError("");
    setStatusDocFile(file);
  };

  const isResignPhk = ["RESIGN", "PHK"].includes(form.status);
  const gantiKeResignPhk = isResignPhk && (worker?.status || "AKTIF") !== form.status;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nomor_pegawai.trim() || !form.nama.trim() || !form.jabatan.trim()) {
      setError("Nomor Pegawai, Nama, dan Jabatan wajib diisi.");
      return;
    }
    if (isResignPhk && !(form.keterangan_status || "").trim()) {
      setError(`Keterangan wajib diisi untuk status ${form.status}.`);
      return;
    }
    if (gantiKeResignPhk && !statusDocFile) {
      setError(`Unggah dokumen bukti (evidence) untuk mengubah status menjadi ${form.status}.`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, usia: form.usia === "" ? null : parseInt(form.usia, 10) };
      await onSave(payload, { photoFile, removePhoto, statusDocFile, sertifikatBaru, hapusSertifikat, berlakuEdit });
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal menyimpan data.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="worker-form-dialog">
        <DialogHeader>
          <DialogTitle>{worker ? "Ubah Data Pekerja" : "Tambah Pekerja Baru"}</DialogTitle>
          <DialogDescription>Lengkapi informasi pekerja di bawah ini.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 flex items-center gap-4" data-testid="photo-section">
            {(() => {
              if (photoPreview) {
                return <img src={photoPreview} alt="Pratinjau foto" data-testid="photo-preview"
                  className="h-16 w-16 rounded-full object-cover border border-slate-200 shrink-0" />;
              }
              if (worker?.photo_path && !removePhoto) {
                return <WorkerAvatar worker={worker} size="h-16 w-16" textSize="text-lg" />;
              }
              return (
                <div className="h-16 w-16 rounded-full bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center shrink-0">
                  <Camera className="h-6 w-6 text-slate-400" />
                </div>
              );
            })()}
            <div className="flex flex-wrap gap-2">
              <input type="file" ref={photoInputRef} accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={pickPhoto} className="hidden" data-testid="photo-file-input" />
              <Button type="button" variant="outline" onClick={() => photoInputRef.current?.click()}
                data-testid="photo-pick-button"
                className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50 h-9">
                <Camera className="h-4 w-4" /> {photoFile || (worker?.photo_path && !removePhoto) ? "Ganti Foto" : "Pilih Foto"}
              </Button>
              {(photoFile || (worker?.photo_path && !removePhoto)) && (
                <Button type="button" variant="outline" onClick={clearPhoto} data-testid="photo-remove-button"
                  className="gap-2 border-rose-200 text-rose-600 hover:bg-rose-50 h-9">
                  <Trash2 className="h-4 w-4" /> Hapus Foto
                </Button>
              )}
              <p className="w-full text-xs text-slate-400">JPG, PNG, WEBP, atau GIF. Maks 5 MB.</p>
            </div>
          </div>

          <Field form={form} set={set} label="Nomor Pegawai" k="nomor_pegawai" placeholder="PEG-001" required />
          <Field form={form} set={set} label="Nama Lengkap" k="nama" placeholder="Nama pekerja" required />
          <Field form={form} set={set} label="Jabatan" k="jabatan" placeholder="Contoh: Supervisor" required />
          <SelectField form={form} set={set} label="Level Pekerja" k="level_pekerja" options={LEVEL_OPTIONS} />
          <div className="space-y-1">
            <SelectField form={form} set={set} label="Lokasi Kerja" k="lokasi_kerja" required
              options={form.lokasi_kerja && !lokasiList.includes(form.lokasi_kerja)
                ? [form.lokasi_kerja, ...lokasiList] : lokasiList} />
            {form.lokasi_kerja && lokasiList.length > 0 && !lokasiList.includes(form.lokasi_kerja)
              ? <p className="text-[11px] text-amber-700" data-testid="lokasi-tidak-terdaftar">Lokasi ini belum ada di daftar resmi — pilih lokasi dari daftar, atau tambahkan dulu di Kelola Admin → Dropdown Lokasi.</p>
              : <p className="text-[11px] text-slate-400">Hanya lokasi dari daftar resmi yang dapat disimpan.</p>}
          </div>
          <Field form={form} set={set} label="Tanggal Masuk" k="tanggal_masuk" type="date" />
          <Field form={form} set={set} label="Tanggal Akhir Kontrak" k="tanggal_akhir_kontrak" type="date" disabled />
          <Field form={form} set={set} label="Tanggal Lahir" k="tanggal_lahir" type="date" />
          <Field form={form} set={set} label="Usia" k="usia" type="number" placeholder="Otomatis dari tgl lahir" />
          <SelectField form={form} set={set} label="Gender" k="gender" options={GENDER_OPTIONS} />
          <SelectField form={form} set={set} label="Pendidikan" k="pendidikan" options={PENDIDIKAN_OPTIONS} />
          <Field form={form} set={set} label="Ukuran Baju" k="ukuran_baju" placeholder="Contoh: M / L / XL" />
          <Field form={form} set={set} label="Ukuran Celana" k="ukuran_celana" placeholder="Contoh: 32" />
          <Field form={form} set={set} label="Ukuran Sepatu" k="ukuran_sepatu" placeholder="Contoh: 42" />
          <Field form={form} set={set} label="Nomor Pertalife" k="nomor_pertalife" placeholder="Nomor Pertalife" />
          <SelectField form={form} set={set} label="Pilihan Opsi" k="pilihan_opsi" options={OPSI_OPTIONS} />
          <Field form={form} set={set} label="Nomor KTP" k="nomor_ktp" placeholder="16 digit" />
          <Field form={form} set={set} label="BPJS Kesehatan" k="bpjs_kesehatan" placeholder="Nomor BPJS Kesehatan" />
          <Field form={form} set={set} label="BPJS Ketenagakerjaan" k="bpjs_ketenagakerjaan" placeholder="Nomor BPJS TK" />
          <Field form={form} set={set} label="Link Maps Lokasi" k="link_maps" placeholder="https://maps.google.com/..." />
          <Field form={form} set={set} label="Keterangan" k="keterangan" placeholder="Catatan tambahan" />
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="alamat" className="text-sm">Alamat</Label>
            <Textarea id="alamat" value={form.alamat ?? ""} onChange={(e) => set("alamat", e.target.value)}
              placeholder="Alamat lengkap" data-testid="field-alamat" rows={2} />
          </div>

          <SertifikasiSection checked={!!form.punya_sertifikasi} onChecked={(v) => set("punya_sertifikasi", v)}
            existing={(worker?.dokumen || []).filter(isSertifikat)} removeIds={hapusSertifikat}
            onRemoveExisting={(id) => setHapusSertifikat((s) => [...s, id])}
            pending={sertifikatBaru} onPendingChange={setSertifikatBaru} onError={setError}
            berlakuEdit={berlakuEdit} onBerlakuEdit={(id, v) => setBerlakuEdit((s) => ({ ...s, [id]: v }))} />

          {/* Status pekerja — paling bawah, tombol berwarna agar terlihat */}
          <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="status-section">
            <Label className="text-sm font-semibold">Status Pekerja <span className="text-rose-500">*</span></Label>
            <Select value={form.status || ""} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="field-status"
                className={`h-11 font-bold tracking-wide ${STATUS_TRIGGER_TONE[form.status] || "bg-slate-600 border-slate-600 text-white"}`}>
                <SelectValue placeholder="Pilih status..." />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`option-status-${o}`}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            {isResignPhk && (
              <div className="space-y-3 rounded-lg border border-rose-200 bg-rose-50/50 p-3" data-testid="resign-phk-section">
                <div className="space-y-1.5">
                  <Label htmlFor="keterangan_status" className="text-sm">
                    Keterangan {form.status} <span className="text-rose-500">*</span>
                  </Label>
                  <Textarea id="keterangan_status" value={form.keterangan_status ?? ""} rows={2}
                    onChange={(e) => set("keterangan_status", e.target.value)}
                    placeholder={`Alasan/keterangan ${form.status.toLowerCase()}...`}
                    data-testid="field-keterangan-status" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    Dokumen Bukti (Evidence) {gantiKeResignPhk && <span className="text-rose-500">*</span>}
                  </Label>
                  <input type="file" ref={statusDocRef} accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={pickStatusDoc} className="hidden" data-testid="status-doc-input" />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" onClick={() => statusDocRef.current?.click()}
                      data-testid="status-doc-pick-button"
                      className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-100 h-9">
                      <Upload className="h-4 w-4" /> {statusDocFile ? "Ganti Dokumen" : "Unggah Dokumen"}
                    </Button>
                    {statusDocFile && (
                      <span className="text-xs font-medium text-slate-700 truncate max-w-[220px]" data-testid="status-doc-name">
                        {statusDocFile.name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">PDF, JPG, PNG, atau WEBP. Maks 10 MB. Tersimpan di bagian Dokumen pekerja.</p>
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-rose-600 font-medium md:col-span-2" data-testid="form-error">{error}</p>}

          <DialogFooter className="md:col-span-2 gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Batal</Button>
            <Button type="submit" disabled={saving} data-testid="form-submit"
              className="bg-blue-600 hover:bg-blue-700 text-white transition-colors">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (worker ? "Simpan Perubahan" : "Tambah Pekerja")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
